// src/pages/Admin/CreateNewOnboarding.js

import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import toast from "react-hot-toast";
import { Formik } from "formik"; // Retain Formik for the overall flow
import { useEffect, useState } from "react";
import Select from "react-select";
import { Log, getCountryList, getEntityFilterConditions, sendDataWithPost, checkUENExists, getActiveBusinessEntity, submitOnboardingStep,
  getTerminalOptions, getCurrentUserEntity
 } from "../../services/services";
import { trimValues } from "../../utils/appUtils";

import api from "../../services/api";
import { urlRegex, INTERNATIONAL_COUNTRY_LIST, MAPPED_REGISTERED_COUNTRY, RISK_LEVEL, MPI_TYPE, SUB_MERCHANT_COUNTRY_CODE } from "../../utils/constants";
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from "libphonenumber-js";
import metadata from 'libphonenumber-js/metadata.full.json'
import '../../pages/Admin/PhoneInput.css';

// Import the step-specific components
import Step1BusinessEntity from "./Step1BusinessEntity";
import Step2Merchant from "./Step2Merchant";
import Step3Terminal from "./Step3Terminal";
import Step4Fees from "./Step4Fees";
import Step4KYC from "./Step4KYC";
import Step5Final from "./Step5Final";
import { downloadMafV370Pdf } from "../../utils/mafPdfV370Export";


// --- Utility Constants (Retained) ---
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const PRIORITY_CODES = ["SG", "MY", "TH"];
const allCountries = getCountries(metadata)
  .map((code) => ({
    code,
    label: regionNames.of(code),
    dialCode: "+" + getCountryCallingCode(code, metadata),
  }))
  .filter((country) => country.dialCode !== "+247");

const priorityCountries = PRIORITY_CODES.map((pCode) =>
  allCountries.find((c) => c.code === pCode)
).filter(Boolean);
const otherCountries = allCountries.filter(
  (c) => !PRIORITY_CODES.includes(c.code)
);
export const COUNTRIES = [...priorityCountries, ...otherCountries];

const APPLICATION_TYPES = [
  { value: 'ONLINE', label: 'ONLINE' },
  { value: 'OFFLINE', label: 'OFFLINE' },
  { value: 'ONLINE_AND_OFFLINE', label: 'ONLINE_AND_OFFLINE' }
];

// Onboarding steps
const steps = [
  'Business Entity',
  'Merchant & Bank Details',
  'Terminal Details',
  'Settlement & Fees',
  'KYC Review',
  'Final Approval'
];
// --- End Utility Constants ---
export const DRAFT_KEY = 'onboarding_draft';

const CreateNewOnboarding = ({ userRole = null }) => {
  // Global State for Stepper
  const [activeStep, setActiveStep] = useState(0);
  const [maxReachedStep, setMaxReachedStep] = useState(0);
  const [onboardingAppId, setOnboardingAppId] = useState(null); // Master Onboarding ID from Step 1
  const [numMerchants, setNumMerchants] = useState(1); // Number of merchants from Step 1
  const [allFormData, setAllFormData] = useState({}); // Stores data for all steps
  const [isLoading, setIsLoading] = useState(false); // Global loading state

  // Local draft storage helpers
  // const DRAFT_KEY = 'onboarding_draft';
  // const loadDraft = () => {
  //   try {
  //     const raw = window.localStorage.getItem(DRAFT_KEY);
  //     if (!raw) return null;
  //     let localData = JSON.parse(raw);
  //     // localData.onboardingAppId = '';
  //     // localData.allFormData[0].business_entity_id = '';
  //     // localData.allFormData[0].onboarding_application_id = '';
  //     setAllFormData(localData.allFormData);
  //     return localData;
  //   } catch (e) {
  //     Log.error('Failed to parse onboarding draft from storage:', e);
  //     return null;
  //   }
  // };

  const loadDraft = () => {
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      let localData = JSON.parse(raw);

      // --- NEW LOGIC: FLATTEN STEP 2 MERCHANT DATA ---
      if (localData.allFormData && localData.allFormData["1"] && Array.isArray(localData.allFormData["1"].items)) {
        // Check if the merchant data is nested (API response structure)
        const firstItem = localData.allFormData["1"].items[0];
        if (firstItem && firstItem.merchant) {
          localData.allFormData["1"].items = localData.allFormData["1"].items.map(item => {
            // Merge fields from 'item' (like 'id') with fields from 'merchant'
            return {
              ...item.merchant,
              id: item.id, // Retain the merchant's ID
            };
          });
        }
      }
      // --- END NEW LOGIC ---

      setAllFormData(localData.allFormData);
      // ... (rest of the loadDraft function remains the same)
      return localData;
    } catch (e) {
      Log.error('Failed to parse onboarding draft from storage:', e);
      return null;
    }
  };
  const saveDraft = (partial) => {
    try {
      const current = loadDraft() || {};
      const next = {
        version: 1,
        updatedAt: new Date().toISOString(),
        activeStep,
        onboardingAppId,
        allFormData,
        ...current,
        ...partial,
      };
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    } catch (e) {
      Log.error('Failed to save onboarding draft to storage:', e);
    }
  };
  const clearDraft = () => {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      Log.error('Failed to clear onboarding draft from storage:', e);
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const handleSnackbarClose = (event, reason) => {
    // Avoid closing on external click (if reason === 'clickaway')
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Original Data fetching states (Retained for Step 1)
  const [countryList, setCountryList] = useState([]);
  const [entityFilterConditions, setEntityFilterConditions] = useState([]);
  const [_, setEntityFilterConditionsLoaded] = useState(false);
  // const [bankAccounts, setBankAccounts] = useState([]);
  // const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const [fetchedApplicationData, setFetchedApplicationData] = useState(null);
  const [activeBusinessEntity, setActiveBusinessEntity] = useState([]);
  const [activeBusinessEntityLoaded, setActiveBusinessEntityLoaded] = useState(false);
  const [terminalOptions, setTerminalOptions] = useState([]);
  const [terminalOptionsLoaded, setTerminalOptionsLoaded] = useState(false);
  const [userOxygen7Entity, setUserOxygen7Entity] = useState(null);
  const [mafExportLoading, setMafExportLoading] = useState(false);

  // Determine if a step is read-only based on userRole
  const isStepReadOnly = (step) => {
      // Only restrict by special role if the user doesn't also have full Edit rights
      if (userRole && !userRole.includes('Edit')) {
          if (userRole.includes('FinalApproval')) {
              return step !== 5; // Only step 5 (Final Approval) is editable
          } else if (userRole.includes('ComplianceCheck')) {
              return step !== 4; // Only step 4 (KYC Review) is editable
          }
      }
      return step > 3; // Steps 0,1,2,3 editable; 4,5 readonly
  };

  const handleDownloadPrefilledMaf = async (currentTerminalValues) => {
    const abeRaw = activeBusinessEntity;
    const abe = Array.isArray(abeRaw) ? abeRaw[0] : abeRaw;
    // Merge: use allFormData[2] for full payment data, overlay live currency fields from Formik
    // New step-3 format uses { merchants: [...] } — just use live values if available
    const terminalsStep = currentTerminalValues || allFormData[2];
    try {
      setMafExportLoading(true);
      await downloadMafV370Pdf(
        {
          businessEntity: allFormData[0],
          merchantsStep: allFormData[1],
          terminalsStep,
          feesStep: allFormData[3],
          countryList,
          activeBusinessEntity: abe,
        },
        `MAF_prefilled_${onboardingAppId || "draft"}.pdf`
      );
      toast.success("MAF PDF downloaded.");
    } catch (e) {
      toast.error(e?.message || "Failed to generate MAF PDF.");
    } finally {
      setMafExportLoading(false);
    }
  };

  const canDownloadMaf =
    Boolean(onboardingAppId && allFormData[0] && allFormData[1]?.items?.length);

  const fetchApplicationData = async (id) => {
    try {
      setIsLoading(true);
      // Call API to get application details by ID
      const response = await api.get(`/merchant/onboarding/${id}/`);
      const appData = response.data;

      setFetchedApplicationData(appData);

      // STEP 1
      const step1Array = appData.step_1_business_entity ? appData.step_1_business_entity : allFormData[0];
      let hasStep1 = false;
      if (step1Array && step1Array.length > 0) {
        const step1RawData = step1Array[0];
        hasStep1 = true;
        // 1. Get detailed Business Entity data
        const businessEntityData = step1RawData.business_entity || {};

        // 2. Merge outer-level fields (e.g., number_of_merchants)
        // with fields inside business_entity.
        const mergedStep1Data = {
          ...businessEntityData,
          application_type: step1RawData.application_type,
          number_of_merchants: step1RawData.number_of_merchants,
          // You can add any other fields from step1RawData if needed
        };

        // 3. Update state allFormData[0] with the merged object
        setAllFormData((prev) => ({
          ...prev,
          0: mergedStep1Data,
        }));

        // Update number of merchants (if needed for Step 2)
        setNumMerchants(mergedStep1Data.number_of_merchants || 0);
        saveDraft({ numMerchants: mergedStep1Data.number_of_merchants || 0 });
      }

      // Determine which step to show next based on available data
      // If step 2 data exists, navigate to step 2; otherwise stay at step 1
      const step2Array = appData.step_2_merchants;
      if (hasStep1 && Array.isArray(step2Array) && step2Array.length > 0) {
        // hydrate step 2 form data format expected by Step2Merchant
        setAllFormData((prev) => ({
          ...prev,
          1: { items: step2Array },
        }));
        setActiveStep(1);
      } else if (hasStep1) {
        setActiveStep(0);
      }

    } catch (error) {
      Log.error('Error fetching application data:', error);
      // Handle errors (e.g., show Snackbar)
    } finally {
      setIsLoading(false);
    }
  };

  // Original Data Fetching Logic (Retained)
  // const setDataBankAccounts = (res) => {
  //   const newData = res?.results?.map((item) => item);
  //   setBankAccounts(newData);
  // }

  // const getBankAccounts = async () => {
  //   const url = `/bank_account/bank_account/filter/`;
  //   sendDataWithPost(url, {}, setDataBankAccounts, setBankAccountsLoaded);
  // }

  useEffect(() => {
    getCountryList(setCountryList);
    getEntityFilterConditions(setEntityFilterConditions, setEntityFilterConditionsLoaded);
    // getBankAccounts();
    getActiveBusinessEntity(setActiveBusinessEntity, setActiveBusinessEntityLoaded);
    getTerminalOptions(setTerminalOptions, setTerminalOptionsLoaded);
    getCurrentUserEntity().then(entity => { if (entity) setUserOxygen7Entity(entity); });

    // Hydrate from local draft to avoid unnecessary API calls
    const draft = loadDraft();
    if (draft) {
      if (typeof draft.activeStep === 'number') setActiveStep(draft.activeStep);
      if (draft.allFormData) setAllFormData(draft.allFormData);
      if (draft.onboardingAppId) setOnboardingAppId(draft.onboardingAppId);
      if (draft.numMerchants) setNumMerchants(draft.numMerchants);
    }

    // Load merchants data from localStorage if available
    const storedMerchants = window.localStorage.getItem('onboarding_merchants');
    if (storedMerchants) {
      try {
        const merchants = JSON.parse(storedMerchants);
        setAllFormData((prev) => ({
          ...prev,
          1: { items: merchants }
        }));
      } catch (e) {
        Log.error('Failed to parse stored merchants data:', e);
      }
    }
  }, []);

  // Keep draft storage in sync when onboardingAppId changes
  useEffect(() => {
    saveDraft({ onboardingAppId });
  }, [onboardingAppId]);

  // Track the furthest step the user has legitimately reached
  useEffect(() => {
    setMaxReachedStep(prev => Math.max(prev, activeStep));
  }, [activeStep]);

  // --- Stepper Navigation ---
  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const next = prevActiveStep + 1;
      saveDraft({ activeStep: next });
      return next;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const next = prevActiveStep - 1;
      saveDraft({ activeStep: next });
      return next;
    });
  };

  /**
   * Main function to handle form submission for the current step,
   * calls the corresponding API, and advances the step.
   * @param {object} values - Formik values from the current step's form.
   * @param {object} actions - Formik actions (setSubmitting).
   */
  const handleComplianceCheck = async (action, values) => {
    setIsLoading(true);
    try {
      const url = `/merchant/onboarding/${onboardingAppId}/save-step-4/`;
      const data = {
        action: action,
        kyc_data: values
      };
      // await api.post(url, data);
      const response = await submitOnboardingStep(url, data);
      // On success, move to next step
      if (response?.status === 200 || response?.status === 201) {
        handleNext();
      }
    } catch (error) {
      Log.error('Error submitting approve/reject decision:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalApprove = async (action, values) => {
    setIsLoading(true);
    try {
      const url = `/merchant/onboarding/${onboardingAppId}/save-step-5/`;
      const data = {
        action: action,
        data: values
      };
      // await api.post(url, data);
      const response = await submitOnboardingStep(url, data);
      // On success, move to next step
      if (response?.status === 200 || response?.status === 201) {
        handleNext();
      }
    } catch (error) {
      Log.error('Error submitting approve/reject decision:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepSubmit = async (values, actions) => {
    setIsLoading(true);
    // Store current step data locally and persist draft
    setAllFormData((prev) => {
      const updated = { ...prev, [activeStep]: values };
      saveDraft({ allFormData: updated });
      return updated;
    });

    try {
      let response;
      let url;
      let newOnboardingId = null;

      // --- Step 1: Business Entity (Creation) ---
      if (activeStep === 0) {
        Log.info(`Submitting Step 1: Business Entity`, values);
        const currentId = values.id;
        const merchant_id_with_source = values.merchant_payout?.map(item => `${item?.id}_${item?.source}`);

        // Prepare data matching the backend's expected structure for save-step-1/
        const businessEntityData = trimValues({
          ...values,
          // last_kyc_review: values.last_kyc_review ? new Date(values.last_kyc_review).toISOString() : null,
          // next_kyc_review: values.next_kyc_review ? new Date(values.next_kyc_review).toISOString() : null,
          // Map Autocomplete/Select values back to IDs/strings for the backend
          registered_country: values.registered_country, // Already an ID
          registered_country_name: (() => {
            const selectedOption = countryList.find(option => option.id === values.registered_country);
            return selectedOption ? MAPPED_REGISTERED_COUNTRY[selectedOption.registered_country] : "";
          })(),
          address_country: values.address_country,
          // application_type: values.application_type.id,
          // Multi-selects map to list of IDs/objects
          // merchant_payout: values.merchant_payout.map(item => item.id),
          merchant_id_with_source: merchant_id_with_source,
          // bank_accounts: values.bank_accounts.map(item => item.id),
          action: currentId ? "update" : "add",
          pk: currentId || "",
        });

        const dataToSend = {
          business_entity_data: businessEntityData,
          number_of_merchants: values.number_of_merchants,
          application_type: values.application_type.id,
        };
        const url = `/merchant/onboarding/save-step-1/`;
        response = await submitOnboardingStep(url, dataToSend);
        newOnboardingId = response.data.id;
        const businessEntityId = response.data.business_entity_id;

        // Validate presence of ID; if missing, treat as an error
        if (!newOnboardingId) {
          throw new Error("Missing Onboarding Application ID in API response. Cannot proceed.");
        }

        // Update Master ID and number of Merchants for subsequent steps
        setOnboardingAppId(newOnboardingId);
        setNumMerchants(values.number_of_merchants);

        // Prepare full step 1 data including all form fields and API response IDs
        const registeredCountryName = (() => {
          const selectedOption = countryList.find(option => option.id === values.registered_country);
          return selectedOption ? MAPPED_REGISTERED_COUNTRY[selectedOption.registered_country] : "";
        })();
        const fullStep1Data = {
          ...businessEntityData,
          onboarding_application_id: newOnboardingId,
          business_entity_id: businessEntityId,
          registered_country_name: registeredCountryName,
        };

        // Update allFormData with the full data
        const updatedAllFormData = {
          ...allFormData,
          0: fullStep1Data,
        };
        setAllFormData(updatedAllFormData);

        // Persist full draft
        saveDraft({
          onboardingAppId: String(newOnboardingId),
          allFormData: updatedAllFormData,
        });
      }
      // --- Step 2: Merchant Details (Update) ---
      else if (activeStep === 1) {
        if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
        // Log.info(`Submitting Step 2: Merchant Details`, values.items);

        // Add merchant_category_name to each merchant
        const merchantsWithNames = values.items.map(merchant => {
          const mcc = activeBusinessEntity?.mcc?.find(m => m.id === merchant.merchant_category_code);
          return {
            ...merchant,
            merchant_category_name: mcc ? mcc.code_description : '',
          };
        });

        // Step 2 API expects a list of merchant objects (values.items is the list)
        const url = `/merchant/onboarding/${onboardingAppId}/save-step-2/`;
        response = await submitOnboardingStep(url, merchantsWithNames);

        // // Store merchants data in localStorage for use in step 3
        // if (response.data && response.data.merchants) {
        //   window.localStorage.setItem('onboarding_merchants', JSON.stringify(response.data.merchants));
        // }

        // Update allFormData with the response merchants
        const updatedAllFormData = { ...allFormData, 1: { items: response.data.merchants } };

        // Propagate updated merchant ids/names into step 3 if already filled
        if (updatedAllFormData[2]?.merchants) {
          updatedAllFormData[2] = {
            ...updatedAllFormData[2],
            merchants: updatedAllFormData[2].merchants.map((m, i) => ({
              ...m,
              merchant_id: updatedAllFormData[1].items[i]?.id || m.merchant_id,
              merchant_name: updatedAllFormData[1].items[i]?.name || m.merchant_name,
            })),
          };
        }

        setAllFormData(updatedAllFormData);

        // Persist draft for step2 like step1
        saveDraft({ allFormData: updatedAllFormData });
      }
      // --- Step 3: Terminal Details ---
      else if (activeStep === 2) {
        if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
        Log.info(`Submitting Step 3: Terminal Details`, values);

        const url = `/merchant/onboarding/${onboardingAppId}/save-step-3/`;
        response = await submitOnboardingStep(url, values);

        // Sync merchant_id / merchant_name from step 2 back into step 3 merchants
        const step3Data = response?.data || values;
        if (step3Data.merchants && allFormData[1]?.items) {
          step3Data.merchants = step3Data.merchants.map((m, i) => ({
            ...m,
            merchant_id: allFormData[1].items[i]?.id || m.merchant_id,
            merchant_name: allFormData[1].items[i]?.name || m.merchant_name,
          }));
        }

        const updatedAllFormData = { ...allFormData, 2: step3Data };
        setAllFormData(updatedAllFormData);
        saveDraft({ allFormData: updatedAllFormData });
      }
      // --- Step 4: Settlement & Fees ---
      else if (activeStep === 3) {
        if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
        Log.info(`Submitting Step 4: Settlement & Fees`, values);

        const url = `/merchant/onboarding/${onboardingAppId}/save-step-fees/`;
        response = await submitOnboardingStep(url, values);

        const feesData = response?.data || values;
        const updatedAllFormData = { ...allFormData, 3: feesData };
        setAllFormData(updatedAllFormData);
        saveDraft({ allFormData: updatedAllFormData });
      }
      // --- Add logic for remaining steps here ---
      // ...
      else {
        // Default action for remaining steps (if API logic isn't defined yet)
        // For Step 4 (Final Submission), use save-step-5-final-decision/
        Log.info(`Skipping API call for Step ${activeStep + 1}`);
      }

      // Move to the next step
      handleNext();

      actions.setSubmitting(false);
      setIsLoading(false);

    } catch (error) {
      Log.error('Submission error:', error);

      let errorMessage = "An unknown error occurred during submission.";

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        //1. Prioritize general errors (detail, non_field_errors)
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors) {
          //Errors are not tied to a specific field (e.g. 'You already have a pending application')
          errorMessage = errorData.non_field_errors.join('; ');
        }
        //2. Handle validation errors of a specific field (for example, UEN errors)
        else {
          const firstKey = Object.keys(errorData)[0];
          if (firstKey) {
            const errorValue = errorData[firstKey];
            const firstError = Array.isArray(errorValue) ? errorValue[0] : errorValue;

            errorMessage = `${firstKey.replace(/_/g, ' ').toUpperCase()}: ${firstError}`;
          }
        }
      } else {
        errorMessage = "Network error or server unreachable. Please check your connection.";
      }

      // --- SHOW ERROR VIA SNACKBAR ---
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });

      // Restore state
      actions.setSubmitting(false);
      setIsLoading(false);
      // Log.error(`Error saving Step ${activeStep + 1}:`, error.response?.data || error.message);
      // alert(`Error saving data. Please check the console for details. Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      actions.setSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    // Shared props derived from original component's state
    const sharedProps = { countryList, COUNTRIES, APPLICATION_TYPES };

    switch (step) {
      case 0:
        return (
            <Step1BusinessEntity
                key={activeStep}
                // Data and submission
                formData={allFormData[0]}
                onNext={handleStepSubmit}
                onSkip={handleNext}
                isSubmitting={isLoading}
                // Original form dependencies
                {...sharedProps}
                entityFilterConditions={entityFilterConditions}
                activeBusinessEntity={activeBusinessEntity}
                readOnly={isStepReadOnly(activeStep)}
            />
        );
      case 1:
        if (!onboardingAppId) return <Typography>Please complete the Business Entity step first.</Typography>;
        return (
          <Step2Merchant
            key={activeStep}
            // Data and submission
            onboardingAppId={onboardingAppId}
            numMerchants={numMerchants}
            formData={allFormData[1]}
            onNext={handleStepSubmit}
            onSkip={handleNext}
            onBack={handleBack}
            isSubmitting={isLoading}
            business_entity_id={allFormData[0]?.business_entity_id}
            // Original form dependencies (if needed, e.g., country lists)
            {...sharedProps}
            activeBusinessEntity={activeBusinessEntity}
            userOxygen7Entity={userOxygen7Entity}
            readOnly={isStepReadOnly(activeStep)}
          />
        );
      case 2:
        return (
          <Step3Terminal
            formData={allFormData[2]}
            onNext={handleStepSubmit}
            onSkip={handleNext}
            onBack={handleBack}
            isSubmitting={isLoading}
            onboardingAppId={onboardingAppId}
            business_entity_id={allFormData[0]?.business_entity_id}
            merchantData={allFormData[1]}
            readOnly={isStepReadOnly(activeStep)}
            onGenerateMaf={handleDownloadPrefilledMaf}
            mafLoading={mafExportLoading}
          />
        );
      case 3:
        return (
          <Step4Fees
            formData={allFormData[3]}
            onNext={handleStepSubmit}
            onBack={handleBack}
            isSubmitting={isLoading}
            readOnly={isStepReadOnly(activeStep)}
          />
        );
      case 4:
        return (
          <Step4KYC
            formData={allFormData[4]}
            onNext={handleComplianceCheck}
            onSkip={handleNext}
            onBack={handleBack}
            isSubmitting={isLoading}
            onboardingAppId={onboardingAppId}
            business_entity_id={allFormData[0]?.business_entity_id}
            activeBusinessEntity={activeBusinessEntity}
            businessEntityName={allFormData[0]?.name}
            readOnly={isStepReadOnly(activeStep)}
          />
        );
      case 5:
        return (
          <Step5Final
            onNext={handleFinalApprove}
            onBack={handleBack}
            isSubmitting={isLoading}
            onboardingAppId={onboardingAppId}
            business_entity_id={allFormData[0]?.business_entity_id}
            businessEntityName={allFormData[0]?.name}
            readOnly={isStepReadOnly(activeStep)}
          />
        );
      default:
        return "Unknown step";
    }
  };

  // helper to control clickable steps — only allow navigation to steps already reached
  const isStepEnabled = (index) => {
    if (index === 0) return true;
    return Boolean(onboardingAppId) && index <= maxReachedStep;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 3, borderRadius: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 4 }}>
          <Typography variant="h4">
            Create New Onboarding Application
          </Typography>
          {activeStep === 3 && (
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              disabled={!canDownloadMaf || mafExportLoading}
              onClick={() => handleDownloadPrefilledMaf()}
            >
              {mafExportLoading ? "Generating…" : "Download prefilled MAF (PDF)"}
            </Button>
          )}
        </Box>

        {/* Custom Stepper UI (Retained from original file) */}
        <Box sx={{
          width: '100%',
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mx: 4
          }}>
            {steps.map((label, index) => {
              const enabled = isStepEnabled(index);
              return (
                <Box
                  key={label}
                  onClick={() => { if (enabled) { if (index === 0) { loadDraft(); } setActiveStep(index); saveDraft({ activeStep: index }); } }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    flex: 1,
                    cursor: enabled ? 'pointer' : 'default',
                    pointerEvents: enabled ? 'auto' : 'auto',
                    opacity: enabled ? 1 : 0.7,
                  }}
                >
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: index === activeStep ? 'primary.main' :
                      index < activeStep ? 'success.main' : 'grey.300',
                    color: index <= activeStep ? 'common.white' : 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    mb: 1,
                    zIndex: 1
                  }}>
                    {index + 1}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: index <= activeStep ? 'text.primary' : 'text.secondary',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem'
                    }}
                  >
                    {label}
                  </Typography>
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        left: '50%',
                        top: 16,
                        borderTop: 2,
                        borderColor: index < activeStep ? 'success.main' : 'grey.300',
                        zIndex: 0
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Form Content Area */}
        <Box sx={{ p: 2 }}>
          {activeStep === steps.length ? (
            (() => {
              // Clear the persisted draft once onboarding is completed
              clearDraft();
              return (
                <Box>
                  <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                    <Typography align="center" textAlign={'center'} sx={{ mt: 2, mb: 1 }}>All steps completed - Application submitted.</Typography>
                  </Grid>
                </Box>
              );
            })()
          ) : (
            <>
              {getStepContent(activeStep)}
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default CreateNewOnboarding;